import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ConsentPurpose, InterviewMode, InterviewLanguageCode } from "@talenthop/shared";
import { CONSENT_SUMMARY, DEFAULT_INTERVIEW_LANGUAGE, INTERVIEW_LANGUAGES, renderConsentSummary } from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";
import { Check, Info, ShieldCheck, Clock, Alert } from "../../components/icons.js";
import type { InviteLanguage } from "./CandidateApp.js";
import { pickableLanguages } from "./interviewLanguage.js";

/** Small inline close glyph — the shared icon set has no X; sized for the modal's close button. */
function CloseX(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

/**
 * Candidate · consent step (POST /invite/{token}/consent). Per-purpose checklist — required
 * purposes must all be granted before continuing; optional ones (profile reuse, model
 * training) stay unchecked by default.
 *
 * "You can withdraw later" is a promise this screen makes, so the copy names WHERE: the
 * completion screen, on this same link (ConsentWithdraw → POST /invite/{token}/consent/withdraw).
 * The promise sat here unkeepable until that endpoint existed — if the withdrawal surface ever
 * moves, this copy moves with it.
 */

/**
 * Bumped v1 → v2 when the notice began stating the recording RETENTION period (30 days, then
 * automatic deletion — actually enforced by the worker sweep, migration 0022). v2 → v3 when the
 * withdrawal right became real (0035): the notice now names where to withdraw and separates
 * "stop processing" from "erase", and it states the decision rule accurately (the engine ranks
 * and proposes; rejection is always a human). notice_version is recorded append-only per consent
 * row and nothing branches on the value, so older 'v1'/'v2' consents stay valid; the bump exists
 * so an auditor can tell which text a candidate actually saw.
 */
const NOTICE_VERSION = "v3";

/**
 * Fallback retention window, used ONLY until the invite payload surfaces the effective value.
 * The real number is server-driven — the API computes it from recordingRetentionDays()
 * (RECORDING_RETENTION_DAYS, default 30) and passes it down as `retentionDays`, so the notice
 * always matches the enforced sweep horizon rather than a hard-coded constant. A notice that
 * promises 30 days while the sweep keeps 90 is the exact failure this whole feature exists to
 * prevent, so we never state a number the server didn't give us.
 */
const DEFAULT_RETENTION_DAYS = 30;

const OPTIONAL_PURPOSES = new Set<ConsentPurpose>(["persona_reuse_per_org", "model_training"]);

/** Purpose copy, parameterised by the effective retention window so the recording notice tracks
 *  the server's enforced deletion horizon rather than a baked-in number. */
const purposeCopy = (retentionDays: number): Record<ConsentPurpose, { title: string; desc: string }> => ({
  interview_capture: {
    title: "Camera & microphone on",
    // "Where your browser supports it" is load-bearing, not hedging: camera-presence detection
    // runs on-device and some browsers cannot do it at all. The old copy promised the observation
    // unconditionally, which was a promise to every candidate whose browser never made one.
    desc: `Your live interview with Asha — an AI interviewer, not a person — is recorded (camera + mic), and we flag integrity signals — like leaving the tab or a second person on camera — for the hiring team to review. Where your browser supports it, we also count how many faces are in the camera frame, on your device only, so we can tell whether you were in view (attention, not appearance — no frame or face data ever leaves your device for this). Flags are advisory — they never reject you; a person reviews them, and your answers are what we assess — never your face, accent or tone. The recording is stored in-region and automatically deleted after ${retentionDays} days.`,
  },
  biometric_liveness: {
    title: "Verify my identity & that I'm live",
    desc: "Face match to your ID + liveness, so no one can impersonate you. Used as proof only — never to score your face or tone.",
  },
  aadhaar_verify: {
    title: "Match my government ID",
    desc: "A one-time identity match with any government ID. Your ID number is never stored — only a verified / not-verified outcome.",
  },
  persona_reuse_per_org: {
    title: "Reuse my verified profile for other roles",
    desc: "Skip re-interviewing next time. You choose who gets access, and you can withdraw anytime.",
  },
  model_training: {
    title: "Allow anonymised model improvement",
    desc: "De-identified answer content may calibrate the assessment engine. Never your identity or media.",
  },
  cross_border_access: {
    title: "Allow cross-border access",
    desc: "Lets an explicitly named overseas team view your verified report. Residency stays in-region.",
  },
  raw_media_transfer: {
    title: "Allow raw media transfer",
    desc: "Lets the hiring org receive the recording itself, not just the report.",
  },
});

export function ConsentStep({
  token,
  purposes,
  retentionDays,
  interviewMode,
  languages,
}: {
  token: string;
  purposes: ConsentPurpose[];
  /** WS4: the languages this requisition offers (invite payload). One entry — or none, from an
   *  older or pre-migration API — means no picker and an unchanged English screen. */
  languages?: InviteLanguage[];
  /** Effective recording-retention window from the invite payload; falls back to the default
   *  until the API surfaces it, so the notice never over- or under-states the sweep horizon. */
  retentionDays?: number;
  /** The requisition's proctoring preset, from the same invite payload. "strict" adds the
   *  disclosure block below — DPDP proportionality requires the mode be named BEFORE consent, not
   *  discovered mid-interview. Absent parses as "natural" (interviewModeOf's default) and the
   *  notice stays exactly what it always was. */
  interviewMode?: InterviewMode;
}): ReactNode {
  const queryClient = useQueryClient();
  const retention = retentionDays ?? DEFAULT_RETENTION_DAYS;
  const strictMode = (interviewMode ?? "natural") === "strict";
  const copy = purposeCopy(retention);
  const items = purposes.map((p) => ({
    purpose: p,
    required: !OPTIONAL_PURPOSES.has(p),
    ...(copy[p] ?? { title: p.replace(/_/g, " "), desc: "" }),
  }));

  const [granted, setGranted] = useState<Record<string, boolean>>(
    Object.fromEntries(items.map((c) => [c.purpose, false])),
  );
  const [termsOpen, setTermsOpen] = useState(false);
  // WS4: the interview language. Chosen HERE, at consent, because it is part of what the candidate
  // consents to (the summary they read, the language they will be recorded answering in) — never
  // mid-interview, where a switch would strand the speech engines on the wrong locale. Default
  // English, the one default everywhere (DEFAULT_INTERVIEW_LANGUAGE).
  const offeredLanguages = pickableLanguages(languages);
  const [language, setLanguage] = useState<InterviewLanguageCode>(DEFAULT_INTERVIEW_LANGUAGE);
  const bilingual = language !== DEFAULT_INTERVIEW_LANGUAGE;
  const requiredItems = items.filter((c) => c.required);
  const optionalItems = items.filter((c) => !c.required);
  const requiredMet = requiredItems.every((c) => granted[c.purpose]);

  const toggleAllRequired = (checked: boolean): void =>
    setGranted((p) => {
      const next = { ...p };
      for (const c of requiredItems) next[c.purpose] = checked;
      return next;
    });

  /** Why the last submit refused the chosen language (reset to English) — cleared on a new pick. */
  const [languageProblem, setLanguageProblem] = useState<string | null>(null);
  const consent = useMutation({
    mutationFn: () =>
      api.post<unknown>(`/invite/${token}/consent`, {
        purposes: items.filter((c) => granted[c.purpose]).map((c) => c.purpose),
        noticeVersion: NOTICE_VERSION,
        // Always sent, English included: the consent row records notice_locale, and "en" written
        // explicitly is a fact where an absent field is only a default. A pre-migration API
        // accepts "en" exactly as before and refuses anything else with a 409 (copy below).
        language,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invite", token] });
    },
    onError: (err) => {
      // A language the recruiter or an owner withdrew between page load and submit: the server
      // refuses it (400/409). Reset the choice to English, refresh the offered list, and say so —
      // never leave a stale picker pointing at a language that will be refused again.
      if (err instanceof ApiError && (err.status === 400 || err.status === 409) && language !== DEFAULT_INTERVIEW_LANGUAGE) {
        setLanguageProblem(
          `${INTERVIEW_LANGUAGES[language].label} is no longer available for this interview — your choice has been reset to English. Pick again if another language is listed, then continue.`,
        );
        setLanguage(DEFAULT_INTERVIEW_LANGUAGE);
        void queryClient.invalidateQueries({ queryKey: ["invite", token] });
      }
    },
  });

  const errorMsg = languageProblem
    ? languageProblem
    : consent.isError && consent.error instanceof ApiError && consent.error.status === 400
      ? "Something in the consent request was invalid. Please re-check the boxes and try again."
      : // The API's pre-migration answer to a non-English choice (inviteConsent.ts): it will not
        // quietly record English consent for a Tamil choice, so neither will this screen — the
        // candidate is told, and English stays available.
        consent.isError && consent.error instanceof ApiError && consent.error.status === 409 && bilingual
        ? `${INTERVIEW_LANGUAGES[language].label} isn't available for this interview yet. You can continue in English, or try again later.`
        : consent.isError
          ? "Couldn't record your consent. Please retry in a moment."
          : null;

  return (
    <div className="cx-step t1c-consent">
      <div className="t1c-consent-head">
        <div>
          <h1 className="t1c-h1">Before you start: your consent</h1>
          <p className="mut t1c-lede">
            Review what happens and what we measure. You're in control: any of these can be withdrawn later
            from your completion screen, on this same link.
          </p>
        </div>
        <button className="btn sm ghost t1c-terms-btn" style={{ flex: "none" }} onClick={() => setTermsOpen(true)}>
          Read full terms
        </button>
      </div>

      {/* WS4 language picker — only when the requisition offers a choice; an English-only role
          (every pre-WS4 one) never sees this block. Radio cards, native name first: the candidate
          is picking the language they THINK in, so it must be readable in that language. */}
      {offeredLanguages.length > 1 && (
        <>
          <h3 className="t1c-h3" style={{ margin: "18px 0 8px" }}>
            Interview language
          </h3>
          <div role="radiogroup" aria-label="Interview language" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {offeredLanguages.map((code) => {
              const l = INTERVIEW_LANGUAGES[code];
              const on = code === language;
              return (
                <label
                  key={code}
                  className="cx-comp-card"
                  style={{
                    margin: 0,
                    padding: "10px 14px",
                    cursor: "pointer",
                    alignItems: "center",
                    gap: 10,
                    border: `1.5px solid ${on ? "var(--teal)" : "transparent"}`,
                    boxShadow: on ? "0 0 0 3px var(--teal-wash)" : "none",
                  }}
                >
                  <input
                    type="radio"
                    name="interview-language"
                    value={code}
                    checked={on}
                    onChange={() => { setLanguageProblem(null); setLanguage(code); }}
                    style={{ margin: 0, width: 16, height: 16, accentColor: "var(--teal)", flex: "none" }}
                  />
                  <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                    <span className="t" lang={l.webSpeechLang} style={{ marginBottom: 0 }}>{l.nativeLabel}</span>
                    {l.nativeLabel !== l.label && <span className="d">{l.label}</span>}
                  </span>
                </label>
              );
            })}
          </div>
          <p className="mut sm" style={{ margin: "8px 0 0" }}>
            {bilingual
              ? `Questions will be spoken in ${INTERVIEW_LANGUAGES[language].label} and you can answer in ${INTERVIEW_LANGUAGES[language].label}. If a question cannot be rendered in time it is asked in English. The hiring team's report carries an English rendering alongside your original words.`
              : "Questions will be spoken in English and you answer in English."}
          </p>
        </>
      )}

      {/* WS4 bilingual notice — the six-point consent SUMMARY in the chosen language, BESIDE the
          English cards below, never instead of them: the English notice is the text of record
          (NOTICE_VERSION), the translation is a reading aid whose own version rides with the
          consent row. Static, human-reviewed text (CONSENT_SUMMARY) — nothing here was generated. */}
      {bilingual && (
        <div className="cx-comp-card" style={{ marginTop: 14, flexDirection: "column", gap: 6 }} lang={INTERVIEW_LANGUAGES[language].webSpeechLang}>
          <div className="t" lang="en">
            In {INTERVIEW_LANGUAGES[language].label} · {INTERVIEW_LANGUAGES[language].nativeLabel}
            <span className="mut sm" style={{ fontWeight: 500, marginLeft: 8 }}>
              summary of the English notice below · {CONSENT_SUMMARY[language].version}
            </span>
          </div>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13.4, lineHeight: 1.6 }}>
            {renderConsentSummary(language, retention).map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ol>
        </div>
      )}

      <h3 className="t1c-h3" style={{ margin: "18px 0 8px" }}>
        What happens in the interview (summary)
      </h3>
      {requiredItems.map((c, i) => (
        <div className="cx-comp-card" key={c.purpose}>
          <span className="ico">{i === 0 ? <ShieldCheck /> : <Alert />}</span>
          <div>
            <div className="t">{c.title}</div>
            <div className="d">{c.desc}</div>
          </div>
        </div>
      ))}
      {/* STRICT disclosure — shown ONLY when the requisition runs the strict preset, and BEFORE
          consent is given (DPDP proportionality: the mode is named where consent is asked, never
          discovered mid-interview). Plain language, and honest about the part candidates worry
          about most: nothing here decides anything — a person reads it all. */}
      {strictMode && (
        <div className="cx-comp-card">
          <span className="ico">
            <ShieldCheck />
          </span>
          <div>
            <div className="t">This interview runs in focus (strict) mode</div>
            <div className="d">
              The hiring team has chosen a distraction-checked session. In plain terms:
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                <li>Your camera and a fullscreen window are needed to start.</li>
                <li>
                  If you leave fullscreen or the camera drops, the interview pauses and waits for
                  you — it never ends on its own, and a technical problem is never held against you.
                </li>
                <li>Copy-paste is switched off in the answer boxes (attempts are noted, nothing is read).</li>
                <li>
                  While you're speaking an answer, whether you're facing the screen is noted — on
                  your own device, camera images never leave it. Thinking time isn't measured, and
                  looking away is recorded as context, not judged: glasses, lighting and people's
                  natural gaze patterns differ.
                </li>
                <li>A person reviews everything. Nothing is decided automatically.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="cx-comp-card">
        <span className="ico">
          <Clock />
        </span>
        <div>
          <div className="t">Environment & session readiness</div>
          <div className="d">
            Find a quiet spot with a stable connection — the interview takes about 30 uninterrupted minutes.
          </div>
        </div>
      </div>

      {optionalItems.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {optionalItems.map((c) => (
            <div className="cx-toggle-row" key={c.purpose}>
              <input
                type="checkbox"
                style={{ marginTop: 3, width: 17, height: 17, accentColor: "var(--teal)", flex: "none" }}
                checked={granted[c.purpose] ?? false}
                onChange={(e) => setGranted((p) => ({ ...p, [c.purpose]: e.target.checked }))}
              />
              <div>
                <div className="t">
                  {c.title} <span className="pill n">Optional</span>
                </div>
                <div className="d">{c.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <label className="consent-item" style={{ marginTop: 8, borderTop: "1px solid var(--line-soft)", paddingTop: 16 }}>
        <input
          type="checkbox"
          checked={requiredMet}
          onChange={(e) => toggleAllRequired(e.target.checked)}
        />
        <div>
          <div className="t">I have read, understood, and accept the guidelines above</div>
          <div className="d">Covers recording, integrity, and identity purposes required for this interview.</div>
        </div>
      </label>

      {errorMsg && (
        <div className="banner crit" style={{ marginTop: 14 }}>
          <Info />
          <span>{errorMsg}</span>
        </div>
      )}

      <button
        className="btn primary t1c-cta"
        style={{ marginTop: 18 }}
        disabled={!requiredMet || consent.isPending}
        onClick={() => consent.mutate()}
      >
        <Check /> {consent.isPending ? "Recording consent…" : "Accept & continue"}
      </button>

      {termsOpen && (
        <div className="cx-modal-backdrop" onClick={() => setTermsOpen(false)}>
          <div className="cx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cx-modal-h">
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
                <ShieldCheck /> Full candidate compliance & terms
              </div>
              <button className="btn sm ghost" onClick={() => setTermsOpen(false)} aria-label="Close" data-tip="Close" data-tip-pos="left">
                <CloseX />
              </button>
            </div>
            <div className="cx-modal-b">
              <h4>1. Purpose & assessment scope</h4>
              <p>
                This interview is conducted by TalentHop AI on behalf of the hiring organization. It measures
                your answers against the role — never your face, tone, or confidence. The engine ranks you and
                can propose that you move forward; a person at the hiring organization reviews and confirms it.
                No one is ever rejected by the engine — every rejection is a human decision.
              </p>
              <h4>2. Audio & video capture</h4>
              <p>
                With your consent, your camera and microphone are recorded during the interview so the hiring
                team can review your answers. Facial analysis, where used, is proof-of-identity only — it never
                scores your face, tone, or personality.
              </p>
              <h4>3. Integrity signals</h4>
              <p>
                We watch for advisory signals during the interview — like leaving the tab, exiting fullscreen, or
                pasted text — and surface them to the hiring team for their review. These never block or interrupt
                you, and never trigger an automatic rejection.
              </p>
              <p>
                Where your browser can do it, we also check how many faces are in the camera frame, about once a
                second. This runs entirely on your own device — no image, video frame or face measurement is sent
                anywhere for it, and it is a count only: it does not recognise you, and it never assesses your
                expression, appearance or mood. If you are out of frame for a while, or someone else appears, we
                show you a note on screen and record it as an advisory flag with how long it lasted. Your interview
                is never paused, and if your browser cannot do this, nothing is recorded and nothing is held
                against you.
              </p>
              <h4>4. Data protection</h4>
              <p>
                Your recording is stored in-region, encrypted at rest and in transit, and automatically deleted{" "}
                {retention} days after your interview. From your completion screen you can withdraw any consent
                on this page, request an export, or ask us to erase your data. Withdrawing stops that processing
                from then on; erasing what is already held is the separate request on the same screen.
              </p>
              <h4>5. Technical requirements</h4>
              <p>
                You'll need a working camera, microphone, and a stable connection. If something goes wrong
                technically, you can request a human follow-up at any point in the flow — nothing is held against
                you for a technical hiccup.
              </p>
            </div>
            <div className="cx-modal-f">
              <button className="btn primary sm" onClick={() => setTermsOpen(false)}>
                <Check /> Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
